/**
 * useColonizationAlarms — İnokulasyon lifecycle milestone alarmları (B5 + B7)
 *
 * B5 — Kontaminasyon risk penceresi (gün 5-14):
 *   İnokulasyon sonrası 5-14. günler arasında "görsel kontrol" hatırlatması.
 *   Ortam nemi %85+ ise escalate eder (yaz sezonu riski).
 *
 * B7 — Kolonizasyon milestone bildirimleri:
 *   Gün 14: "Myselium kontrol — Aslan Yelesi tam kolonizasyona yakın"
 *   Gün 21: "Fruiting trigger hazırlığı — FAE artır, RH %92+'ya çıkar"
 *
 * Her bag için günde 1 push (localStorage key: gmc_coloniz_{bagId}_{date}_{type})
 */

import { useEffect, useRef } from 'react';
import { useAppStore } from '../store';
import { sendTelegramMessage } from '../services/notificationService';
import { SPECIES_INCUBATION_DAYS } from '../services/thresholds';
import type { AlertNotificationSettings } from '../types';

const CHECK_INTERVAL_MS = 60 * 60 * 1000; // saatte bir
const LS_PREFIX = 'gmc_coloniz';
const EARLY_SCAN_DAY = 3;
const HIGH_RISK_START = 5;
const HIGH_RISK_END = 14;
const HIGH_RH_THRESHOLD = 85; // bu üstünde kontaminasyon riski artar

interface Opts {
  notifSettings: AlertNotificationSettings;
}

export function useColonizationAlarms({ notifSettings }: Opts): void {
  const growBags = useAppStore(s => s.growBags);
  const current = useAppStore(s => s.current);
  const growBagsRef = useRef(growBags);
  const currentRef = useRef(current);
  growBagsRef.current = growBags;
  currentRef.current = current;

  useEffect(() => {
    if (!notifSettings.telegram?.enabled) return;

    const check = async () => {
      const today = new Date().toISOString().slice(0, 10);
      const currentRH = currentRef.current?.humidity ?? 0;
      const highRH = currentRH >= HIGH_RH_THRESHOLD;

      for (const bag of growBagsRef.current) {
        if (bag.status !== 'incubation') continue;
        if (bag.speciesId !== 'lion-mane') continue;

        const daysIn = Math.floor(
          (Date.now() - new Date(bag.inoculationDate).getTime()) / 86400000
        );
        const speciesData = SPECIES_INCUBATION_DAYS[bag.speciesId];

        // ── B5-pre: Gün 3 ilk erken kontaminasyon taraması ──────────────────
        if (daysIn === EARLY_SCAN_DAY) {
          const key = `${LS_PREFIX}_${bag.id}_${today}_earlyscan`;
          if (!localStorage.getItem(key)) {
            const lines = [
              `🔍 <b>ERKEN KONTAMİNASYON TARAMASI — ${bag.name}</b>`,
              ``,
              `📅 İnokulasyon: <b>${daysIn}. gün</b> (Trichoderma 48-72h penceresi)`,
              ``,
              `Trichoderma inokülasyondan sonraki 48-72 saat içinde kendini gösterir (hızlı gelişim).`,
              `Bu aşamada henüz beyaz olan misel yeşile dönmeden yakalamak en yüksek verim kazancını sağlar.`,
              ``,
              `🔎 Erken Teşhis Belirtileri:`,
              `• Agresif hızlı yayılan parlak tebeşir beyazı tabakalar (miselden daha kaba ve hızlı)`,
              `• Henüz sporlanmamış (yeşile dönmemiş) beyaz pamuksu yapılar`,
              `• Torbanın herhangi bir yerinde şüpheli hızlı kolonizasyon`,
            ];
            localStorage.setItem(key, '1');
            await sendTelegramMessage(lines.join('\n'));
          }
        }

        // ── B5: Kontaminasyon risk penceresi (gün 5-14) ──────────────────────
        if (daysIn >= HIGH_RISK_START && daysIn <= HIGH_RISK_END) {
          const key = `${LS_PREFIX}_${bag.id}_${today}_riskwindow`;
          if (!localStorage.getItem(key)) {
            const urgency = highRH
              ? `⚠️ <b>Yüksek risk + yüksek nem!</b> Bugün mutlaka kontrol et.`
              : `🔍 Risk penceresi aktif — rutin görsel kontrol yap.`;

            const lines = [
              `🦠 <b>KONTAMİNASYON RİSK PENCERESİ — ${bag.name}</b>`,
              ``,
              `📅 İnokulasyon: <b>${daysIn}. gün</b> (kritik pencere: gün 5-14)`,
              highRH ? `💧 Ortam nemi: <b>${currentRH.toFixed(0)}%</b> — yüksek nem riski artırıyor` : '',
              ``,
              urgency,
              ``,
              `🔎 Kontrol listesi:`,
              `• Yeşil/siyah leke → dışarıya kapalı poşette at`,
              `• Gri ince ağ (cobweb) → izole et, zarar vermez`,
              `• Kaygan/ekşi koku → ıslak çürük, at`,
              `• Parlak beyaz misel → ✅ normal`,
            ].filter(Boolean);

            localStorage.setItem(key, '1');
            await sendTelegramMessage(lines.join('\n'));
          }
        }

        // ── B7: Kolonizasyon milestone — Gün 14 ──────────────────────────────
        if (daysIn === speciesData?.minDays) {
          const key = `${LS_PREFIX}_${bag.id}_${today}_day14`;
          if (!localStorage.getItem(key)) {
            const lines = [
              `🔬 <b>KOLONİZASYON MİLESTONE — ${bag.name}</b>`,
              ``,
              `📅 <b>${daysIn}. gün</b> — Aslan Yelesi minimum kolonizasyon eşiği`,
              ``,
              `Kontrol: Tüm substrat yüzeyi beyaz misel ile kaplanmış mı?`,
              `• Tam kaplama ✅ → 7 gün daha bekle, sonra fruiting trigger`,
              `• Kısmi kaplama 🔄 → Normal, 21. güne kadar izle`,
              `• Hiç yok / renkli ❌ → Kontaminasyon şüphesi`,
            ];
            localStorage.setItem(key, '1');
            await sendTelegramMessage(lines.join('\n'));
          }
        }

        // ── B7: Kolonizasyon milestone — Gün 21 ──────────────────────────────
        if (daysIn === speciesData?.typicalDays) {
          const key = `${LS_PREFIX}_${bag.id}_${today}_day21`;
          if (!localStorage.getItem(key)) {
            const lines = [
              `🚀 <b>FRUİTİNG TRİGGER HAZIRLIĞI — ${bag.name}</b>`,
              ``,
              `📅 <b>${daysIn}. gün</b> — Aslan Yelesi tipik kolonizasyon tamamlandı`,
              ``,
              `Fruiting trigger adımları:`,
              `1. Tent sıcaklığını 23°C → <b>14°C</b>'ye düşür (soğuk şok)`,
              `2. RH'yi %72 → <b>%95</b>'e çıkar`,
              `3. CO₂'yi <b>800 ppm altına</b> düşür (FAE artır)`,
              `4. Işık ekle (dolaylı, orta)`,
              ``,
              `Pin oluşumu 5-10 gün içinde beklenir.`,
            ];
            localStorage.setItem(key, '1');
            await sendTelegramMessage(lines.join('\n'));
          }
        }
      }
    };

    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [notifSettings.telegram?.enabled]);
}
